import { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useContact } from "@/context/ContactContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactDialog() {
  const { open, setOpen } = useContact();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.dismiss();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please add your name and a message.");
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setOpen(false);
      setForm({ name: "", email: "", message: "" });
      toast.success("Thanks! We'll be in touch shortly.");
    }, 700);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="contact-dialog"
        className="sm:max-w-[480px] bg-white border border-black/10 text-[#0a0a0a] rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black tracking-tight">
            Get in touch
          </DialogTitle>
          <DialogDescription className="text-zinc-600">
            Questions about OpenDeposit or the Actalink ecosystem? Drop us a line.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="c-name" className="text-zinc-700">Name</Label>
            <Input
              id="c-name"
              data-testid="contact-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Satoshi Nakamoto"
              className="bg-zinc-50 border-black/10 text-[#0a0a0a] placeholder:text-zinc-400 focus-visible:ring-[#f5d949] h-11 rounded-xl"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="c-email" className="text-zinc-700">Email</Label>
            <Input
              id="c-email"
              type="email"
              data-testid="contact-email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@wallet.xyz"
              className="bg-zinc-50 border-black/10 text-[#0a0a0a] placeholder:text-zinc-400 focus-visible:ring-[#f5d949] h-11 rounded-xl"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="c-message" className="text-zinc-700">Message</Label>
            <Textarea
              id="c-message"
              data-testid="contact-message"
              value={form.message}
              onChange={update("message")}
              placeholder="How can we help?"
              rows={4}
              className="bg-zinc-50 border-black/10 text-[#0a0a0a] placeholder:text-zinc-400 focus-visible:ring-[#f5d949] rounded-xl resize-none"
            />
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#f5d949] text-black font-bold py-3.5 hover:bg-[#f7e16d] transition-colors disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send message"}
            {!submitting && <PaperPlaneTilt size={18} weight="bold" />}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
